import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../app/configuration/get-environment.util';
import { getLogoutHandoffUrl } from './logout-handoff';
import { absolutizeUri, buildFallbackLogoutUrl } from './logout-url.util';

const SILENT_INIT_TIMEOUT_MS = 8000;

/**
 * Independent Keycloak logout for the bootstrap-error page. Tries, in order:
 *  1. A handoff URL the live app stashed while it still had an authenticated
 *     session (LogoutHandoffBootstrapper) — instant, and doesn't depend on
 *     any cross-origin cookie access at all.
 *  2. A fresh, self-contained keycloak-js re-init: the failed app's injector
 *     (and its Keycloak instance) is unreachable, so this constructs a new
 *     instance and silently re-establishes the session to recover an
 *     idToken from the Keycloak SSO cookie directly.
 * Either path, when it recovers a token, lets the adapter build the logout
 * URL WITH id_token_hint (Keycloak 18+ skips its confirmation screen only
 * then). If neither succeeds, falls back to a plain confirmation-screen URL.
 */
@Injectable()
export class BootstrapErrorLogoutService {
  public async logout(): Promise<void> {
    const handoffUrl = getLogoutHandoffUrl();
    if (handoffUrl) {
      window.location.assign(handoffUrl);
      return;
    }

    const { keycloakUrl, realm, clientId, silentCheckSsoRedirectUri } =
      environment.authorization;

    try {
      const keycloak = new Keycloak({ url: keycloakUrl, realm, clientId });

      const authenticated = await this.withTimeout(
        keycloak.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: absolutizeUri(silentCheckSsoRedirectUri),
          // Never full-page-redirect from an error page if 3rd-party cookies are blocked.
          silentCheckSsoFallback: false,
          // No session-status iframe needed for a one-shot logout.
          checkLoginIframe: false,
          // Mirrors KeycloakSessionProviderService.initialize().
          useNonce: false,
          messageReceiveTimeout: 5000,
        }),
        SILENT_INIT_TIMEOUT_MS
      );

      if (authenticated && keycloak.idToken) {
        // GET adapter: window.location.replace(createLogoutUrl()) — includes
        // id_token_hint because keycloak.idToken is set -> no confirmation screen.
        await keycloak.logout({ redirectUri: `${window.location.origin}/` });
        return; // navigation in flight
      }
    } catch (error) {
      console.warn(
        '[BootstrapErrorLogoutService] Silent session recovery failed, falling back to plain logout:',
        error
      );
    }

    // Degraded path: no id_token_hint available -> Keycloak shows its
    // one-click confirmation page, but the SSO session still ends.
    window.location.assign(
      buildFallbackLogoutUrl({ keycloakUrl, realm, clientId })
    );
  }

  private withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return Promise.race([
      promise,
      new Promise<never>((_, reject) =>
        setTimeout(
          () =>
            reject(new Error(`Keycloak silent init timed out after ${ms}ms`)),
          ms
        )
      ),
    ]);
  }
}
